import {computed, Injectable, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {defer, merge, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {dependOn} from '../../../../common/rxjs/depend-on';
import {DEX_RATE_REF_SYMBOL} from '../const';
import {DexStreamingService} from '../dex-streaming.service';
import {DexMdSubjectService} from '../subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from '../subjects/dex-securities-subject-service';
import {ComputedHeldPosition, ComputedTrancheHeldPosition} from '../types/computed/computed-held-position';
import {ComputedTranche} from '../types/computed/computed-tranche';
import {Deliverable} from '../types/deliverable';
import {HeldPosition} from '../types/held-position';
import {Tranche} from '../types/tranche';
import {mathWithDiffDpDecimalJs, percentValueWithDel, ValueWithDel} from '../utils';
import {DexComputedHeldPositionService} from './dex-computed-held-position.service';
import {DexComputedTrancheRulesService} from './dex-computed-tranche-rules.service';

@Injectable()
export class DexComputedTrancheService {

    public readonly list$: Observable<ComputedTranche[]>;
    public readonly $list: Signal<ComputedTranche[]>;

    private readonly DEFAULT_TOTAL_VALUE_DEL_SYMBOL = 'PORTFOLIO_USD';

    private readonly $dependenciesUpdated = toSignal(merge(
        this.dexSecuritiesSubjectService.deliverablesUpdated$,
        this.dexSecuritiesSubjectService.tradablesUpdated$,
        this.dexMdSubjectService.tranchesUpdated$,
        this.dexMdSubjectService.positionsUpdated$,
        this.dexMdSubjectService.trancheDelRulesUpdated$,
        this.dexMdSubjectService.trancheRulesUpdated$
    ), {equal: () => false});

    private readonly $trancheRulesUpdated = toSignal(
        this.dexMdSubjectService.trancheRulesUpdated$,
        {equal: () => false}
    );

    constructor(
        private dexSecuritiesSubjectService: DexSecuritiesSubjectService,
        private dexMdSubjectService: DexMdSubjectService,
        private dexStreamingService: DexStreamingService,
        private dexComputedHeldPositionService: DexComputedHeldPositionService,
        private dexComputedTrancheRulesService: DexComputedTrancheRulesService
    ) {
        this.list$ = this.createListStream();
        this.$list = toSignal(this.list$, {initialValue: []});
    }

    public create(tranche: Tranche): ComputedTranche {
        const unitDeliverable = this.dexSecuritiesSubjectService.getDeliverableById(tranche.unitDeliverableId);
        const unitPriceDeliverable = this.dexSecuritiesSubjectService.getDeliverableById(tranche.unitPriceDeliverableId);
        const unitTradeable = this.dexSecuritiesSubjectService.getTradableById(tranche.unitTradeableId);

        const $calc = computed(() => {
            this.$dependenciesUpdated();

            const posMap = this.dexMdSubjectService.posMapByAccountId.get(tranche.reservedAccountId);
            const totalValueCalc = this.calcTotalValue(tranche, posMap);
            const {computedBasePos, computedPositions} = this.getTranchePositions(tranche, posMap, totalValueCalc);

            const totalUnitsValue = -BigInt(computedBasePos?.raw.balance || 0);

            const totalValue = totalValueCalc.v;

            const unitPrice = totalValue && totalUnitsValue
                ? mathWithDiffDpDecimalJs(
                    totalValue,
                    totalValueCalc.d?.decimalPlaces || 0,
                    totalUnitsValue,
                    unitDeliverable?.decimalPlaces || 0,
                    unitPriceDeliverable?.decimalPlaces || 0,
                    (v1, v2) => v1.div(v2)
                )
                : 0n;

            return {
                computedPositions,
                totalValue,
                totalValueDeliverable: totalValueCalc.d,
                totalUnits:            totalUnitsValue,
                unitPrice,
                computedBasePos
            };
        });

        const $trancheRules = computed(() => {
            this.$trancheRulesUpdated();

            return this.dexComputedTrancheRulesService.createByTrancheId(tranche.poolTrancheId);
        });

        return {
            raw: tranche,
            unitDeliverable,
            unitPriceDeliverable,
            unitTradeable,
            $computedBasePosition: computed(() => $calc()?.computedBasePos),
            $computedPositions: computed(() => $calc()?.computedPositions),
            $totalUnits: computed(() => $calc()?.totalUnits),
            $totalValue: computed(() => $calc()?.totalValue),
            $totalValueDeliverable: computed(() => $calc()?.totalValueDeliverable),
            $unitPrice:             computed(() => $calc()?.unitPrice),
            $trancheRules
        };
    }

    private createListStream(): Observable<ComputedTranche[]> {
        const build = () => this.buildList();

        return merge(
            defer(() => build()),
            this.dexSecuritiesSubjectService.deliverablesUpdated$,
            this.dexSecuritiesSubjectService.tradablesUpdated$,
            this.dexMdSubjectService.tranchesUpdated$
        ).pipe(
            map(build),
            dependOn(this.dexStreamingService.stream$)
        );
    }

    private buildList(): ComputedTranche[] {
        const list: ComputedTranche[] = [];

        this.dexMdSubjectService.trancheMapById.forEach(tranche => {
            list.push(
                this.create(tranche)
            );
        });

        return list;
    }

    private calcTotalValue(tranche: Tranche, posMap: Map<string, HeldPosition> | undefined): ValueWithDel {
        let total = BigInt(0);
        let del: Deliverable | undefined = undefined;

        if (posMap) {
            for (const pos of posMap.values()) {
                if (pos.identifier.deliverableId !== tranche.unitDeliverableId) {
                    total += BigInt(pos.portfolioValue);
                    del ??= this.dexSecuritiesSubjectService.getDeliverableById(pos.valueDeliverableId);
                }
            }
        }

        del ??= this.dexSecuritiesSubjectService.getDeliverableBySymbol(this.DEFAULT_TOTAL_VALUE_DEL_SYMBOL)
            || this.dexSecuritiesSubjectService.getDeliverableBySymbol(DEX_RATE_REF_SYMBOL);

        return {
            v: total,
            d: del
        };
    }

    private getTranchePositions(
        tranche: Tranche,
        posMap: Map<string, HeldPosition> | undefined,
        totalValueCalc: ValueWithDel
    ): {
        computedBasePos: ComputedTrancheHeldPosition | undefined;
        computedPositions: ComputedTrancheHeldPosition[];
    } {
        const result: {
            computedBasePos: ComputedTrancheHeldPosition | undefined;
            computedPositions: ComputedTrancheHeldPosition[];
        } = {
            computedBasePos: undefined,
            computedPositions: []
        };

        if (posMap) {
            // Resolve only computedBasePos
            for (const pos of posMap.values()) {
                if (pos.identifier.deliverableId === tranche.unitDeliverableId) {
                    const computedPos = this.createTranchePos(tranche, undefined, pos, totalValueCalc);
                    result.computedBasePos = computedPos;
                    result.computedPositions.unshift(computedPos);
                }
            }
            // Resolve other positions, as they depend on the computedBasePos
            for (const pos of posMap.values()) {
                if (pos !== result.computedBasePos?.raw) {
                    result.computedPositions.push(
                        this.createTranchePos(tranche, result.computedBasePos, pos, totalValueCalc)
                    );
                }
            }
        }

        return result;
    }

    private createTranchePos(
        tranche: Tranche,
        computedBasePos: ComputedHeldPosition | undefined,
        pos: HeldPosition,
        totalValueCalc: ValueWithDel
    ): ComputedTrancheHeldPosition {
        const computedPos: ComputedTrancheHeldPosition = {
            ...this.dexComputedHeldPositionService.create(pos),
            percent: undefined,
            isDepositAllowed: undefined
        };

        if (computedBasePos) {
            computedPos.percent = this.calcPosPercent(computedPos, totalValueCalc);

            computedPos.isDepositAllowed = this.dexMdSubjectService.trancheDeliverableRulesMapById
                .get(tranche.poolTrancheId)
                ?.get(pos.identifier.deliverableId)
                ?.participationRules?.depositsAllowed;
        }

        return computedPos;
    }

    private calcPosPercent(
        computedPos: ComputedHeldPosition,
        totalValueCalc: ValueWithDel
    ): number {
        return percentValueWithDel(
            {
                v: BigInt(computedPos.raw.portfolioValue),
                d: computedPos.valueDeliverable
            },
            totalValueCalc
        );
    }

}
